import {
  MOBILE_SCROLL_PROGRESS_VIEWPORT_WEIGHT,
  SCROLL_PROGRESS_MAX,
  SCROLL_PROGRESS_MIN,
  TIMELINE_PROGRESS_FILL_SELECTOR,
  TIMELINE_PROGRESS_TRACK_SELECTOR,
  TIMELINE_STEP_MARKER_SELECTOR,
} from "./constants";
import { createEventManager } from "./eventManager";
import { isDesktopViewport } from "./mediaQueries";

const ACTIVE_MARKER_CLASS = "bg-primary-300";
const INACTIVE_MARKER_CLASS = "bg-neutral-100";

const clampProgress = (progressValue) =>
  Math.min(SCROLL_PROGRESS_MAX, Math.max(SCROLL_PROGRESS_MIN, progressValue));

const computeDesktopProgress = (element) => {
  const scrollRange = element.scrollHeight - element.clientHeight;

  if (scrollRange <= SCROLL_PROGRESS_MIN) {
    return SCROLL_PROGRESS_MAX;
  }

  return clampProgress(element.scrollTop / scrollRange);
};

const computeMobileProgress = (element, win) => {
  const viewportHeight = win.innerHeight;
  const contentRect = element.getBoundingClientRect();
  const traversedDistance = viewportHeight - contentRect.top;
  const totalDistance =
    contentRect.height +
    viewportHeight * MOBILE_SCROLL_PROGRESS_VIEWPORT_WEIGHT;

  if (totalDistance <= SCROLL_PROGRESS_MIN) {
    return SCROLL_PROGRESS_MIN;
  }

  return clampProgress(traversedDistance / totalDistance);
};

export const createScrollProgressController = ({
  element,
  win,
  scrollsInPlace = () => isDesktopViewport(win),
}) => {
  const fillElement = element.querySelector(TIMELINE_PROGRESS_FILL_SELECTOR);
  const trackElement = element.querySelector(TIMELINE_PROGRESS_TRACK_SELECTOR);
  const markerElements = [
    ...element.querySelectorAll(TIMELINE_STEP_MARKER_SELECTOR),
  ];

  if (!fillElement) {
    return {
      update: () => {},
      destroy: () => {},
    };
  }

  const events = createEventManager();
  const updateMarkerStates = (progressValue) => {
    if (!trackElement || markerElements.length === 0) {
      return;
    }

    const trackRect = trackElement.getBoundingClientRect();
    const fillBottomPosition = trackRect.top + trackRect.height * progressValue;

    markerElements.forEach((markerElement) => {
      const markerRect = markerElement.getBoundingClientRect();
      const markerCenterPosition = markerRect.top + markerRect.height / 2;
      const isMarkerReached = markerCenterPosition <= fillBottomPosition;

      markerElement.classList.toggle(ACTIVE_MARKER_CLASS, isMarkerReached);
      markerElement.classList.toggle(INACTIVE_MARKER_CLASS, !isMarkerReached);
    });
  };

  const update = () => {
    const progressValue = scrollsInPlace()
      ? computeDesktopProgress(element)
      : computeMobileProgress(element, win);

    fillElement.style.transform = `scaleY(${progressValue})`;
    updateMarkerStates(progressValue);
  };

  events.add(element, "scroll", update, { passive: true });
  events.add(win, "scroll", update, { passive: true });
  events.add(win, "resize", update);
  update();

  return {
    update,
    destroy: () => {
      events.cleanup();
    },
  };
};
